import { MAX_RUNNER_NAME_LENGTH } from '../game/callsigns';
import { type ApiUser } from '../services/api';
import { escapeHtml } from './puzzleScreen';

export interface SettingsView {
  readonly runnerName: string;
  readonly soundOn: boolean;
  readonly user: ApiUser | null;
  readonly online: boolean;
}

function accountLine(view: SettingsView): string {
  if (!view.online) return 'Offline. Your runs stay on this device until the server is back.';
  if (view.user === null || view.user.isGuest) {
    return 'Guest runner. Register to keep your progress and sign in from another device.';
  }
  return `Signed in as ${escapeHtml(view.user.email ?? 'a registered runner')}.`;
}

export function settingsMarkup(view: SettingsView): string {
  return `
    <div class="screen screen--brief settings">
      <header class="sub-header">
        <button class="btn btn--ghost btn--icon" data-action="back" aria-label="Back to the map">←</button>
        <div>
          <h1>Runner's Kit</h1>
          <p>Name, sound, account</p>
        </div>
      </header>
      <form class="form" data-form="runner" novalidate>
        <h2 class="form__title">Runner name</h2>
        <p class="form__hint">
          How Gearfall knows you${view.online ? ', on this device and on your account' : ''}.
          Take the suggestion or write your own, up to ${MAX_RUNNER_NAME_LENGTH} characters.
        </p>
        <label class="field">
          <span>Name</span>
          <input type="text" name="runnerName" id="runner-name" value="${escapeHtml(view.runnerName)}"
                 required maxlength="${MAX_RUNNER_NAME_LENGTH}" autocomplete="nickname" spellcheck="false" />
        </label>
        <p class="form__error" data-role="error" role="alert" hidden></p>
        <p class="form__hint form__status" data-role="status" role="status" hidden></p>
        <div class="brief__actions">
          <button type="button" class="btn btn--ghost" data-action="suggest">Suggest another</button>
          <button type="submit" class="btn btn--primary">Save name</button>
        </div>
      </form>
      <section class="form" aria-labelledby="settings-sound">
        <h2 class="form__title" id="settings-sound">Sound</h2>
        <label class="switch">
          <input type="checkbox" data-role="sound"${view.soundOn ? ' checked' : ''} />
          <span class="switch__track" aria-hidden="true"></span>
          <span class="switch__label">Sound effects</span>
        </label>
        <p class="form__hint">Clicks and chimes while you play. Off unless you switch them on.</p>
      </section>
      <section class="form" aria-labelledby="settings-account">
        <h2 class="form__title" id="settings-account">Account</h2>
        <p class="form__hint">${accountLine(view)}</p>
        <div class="brief__actions">
          <a class="btn btn--ghost" href="#/account">Runner's papers</a>
        </div>
      </section>
    </div>
  `;
}

export function showFormStatus(container: ParentNode, message: string | null): void {
  const el = container.querySelector<HTMLElement>('[data-role="status"]');
  if (!el) return;
  el.textContent = message ?? '';
  el.hidden = message === null;
}
